import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, CalendarDays, ChevronRight, Check, Lightbulb } from 'lucide-react';
import { api } from '../lib/api';
import { useProgramBranding } from './program-branding';
import { Icon } from '../components/Icon';
import { Button, Card, ErrorState, GradientBar, Modal, Spinner, cx } from '../components/ui';
import { Markdown } from '../components/Markdown';
import { shortLabel } from '@trellis/shared';
import { formatDateLong, formatRelative } from '../lib/format';

interface HomeResponse {
  patient: { fullName: string | null; cancerType: string | null; status: string };
  branding: { programName: string; logoUrl: string | null } | null;
  menu: Array<{ id: string; label: string; icon: string; type: string; target: string }>;
  /** Sus programas activos. Con dos, cada indicador viene rotulado con el suyo. */
  programs: Array<{
    enrollmentId: string;
    id: string;
    slug: string;
    name: string;
    brand: string | null;
    form: { id: string; displayName: string } | null;
  }>;
  /** Las preguntas de una sola vez que todavía no contestó. */
  onboarding: Array<{
    id: string;
    label: string;
    help: string | null;
    required: boolean;
    program: { id: string; name: string; brand: string | null } | null;
  }>;
  indicators: Array<{
    id: string;
    label: string;
    group: string | null;
    type: string;
    unit: string | null;
    frequency: string;
    program: { id: string; name: string; brand: string | null } | null;
    doneToday: boolean;
    pending: boolean;
  }>;
  tipOfTheDay: { id: string; title: string; description: string } | null;
  pendingConsents: Array<{ id: string; kind: string; version: string; title: string; body: string }>;
  nextEvent: { id: string; title: string; scheduledAt: string; location: string | null } | null;
}

export function PatientHome() {
  const { slug } = useParams<{ slug: string }>();
  const branding = useProgramBranding();
  const queryClient = useQueryClient();

  const home = useQuery({
    queryKey: ['me', 'home'],
    queryFn: () => api.get<HomeResponse>('/me/home'),
  });

  /** Si abrió la lista de todo lo demás que puede cargar cuando quiera. */
  const [verTodo, setVerTodo] = useState(false);

  const acceptConsent = useMutation({
    mutationFn: (documentId: string) => api.post(`/me/consents/${documentId}/accept`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'home'] }),
  });

  if (home.isError) return <ErrorState error={home.error} onRetry={() => home.refetch()} />;
  if (home.isLoading || !home.data) return <Spinner />;

  const data = home.data!;
  const base = `/${slug}/app`;
  const firstName = data.patient.fullName?.split(' ')[0] ?? '';
  const pendingToday = data.indicators.filter((indicator) => indicator.pending);
  /** Todo lo demás: lo ya hecho hoy y lo que no se pide todos los días. */
  const otros = data.indicators.filter((indicator) => !indicator.pending);

  /**
   * Con dos tratamientos activos, cada cosa se rotula con el suyo.
   *
   * Con uno solo no hace falta: repetir el nombre del programa en cada fila
   * cuando hay uno solo es ruido que no distingue nada.
   */
  const showTreatment = data.programs.length > 1;

  return (
    <div className="max-w-lg lg:max-w-3xl mx-auto">
      <header className="px-5 pt-6 pb-5 safe-top">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-fg-muted">{greeting()}</p>
            <h1 className="text-2xl font-semibold tracking-tight truncate">{firstName}</h1>
          </div>
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.programName}
              className="h-9 object-contain shrink-0"
            />
          ) : (
            <span className="font-semibold text-primary shrink-0">{branding.programName}</span>
          )}
        </div>
        <GradientBar className="mt-4" />
      </header>

      <div className="px-5 space-y-5">
        {/* El consentimiento se pide antes que nada: sin él, el paciente figura
            solo como invitado y su participación no está formalizada. */}
        {data.pendingConsents.length > 0 && (
          <ConsentPrompt
            consent={data.pendingConsents[0]!}
            onAccept={(id) => acceptConsent.mutate(id)}
            accepting={acceptConsent.isPending}
          />
        )}

        {/* Reportar un efecto va arriba de todo.
            Es la única acción de la app con un plazo legal detrás: el
            institución tiene 24 horas para notificar un evento adverso grave.
            Alguien que se siente mal no debería tener que pasar por sus tareas
            pendientes para encontrar dónde decirlo.

            El tono es sobrio a propósito: en rojo y con signos de alarma
            desalienta el reporte de lo leve, que es lo que más se subnotifica. */}
        <Link to={`${base}/reportar`} className="block">
          <Card className="p-4 flex items-center gap-3 border-warning bg-warning-soft">
            <div className="size-10 rounded-md bg-surface-raised text-warning grid place-items-center shrink-0">
              <AlertTriangle className="size-5" />
            </div>
            <div className="min-w-0 grow">
              <p className="font-semibold text-[15px]">¿Tenés algún malestar o efecto?</p>
              <p className="text-sm text-fg-muted leading-snug">
                Contale a tu equipo, por chico que parezca
              </p>
            </div>
            <ChevronRight className="size-4 text-fg-subtle shrink-0" />
          </Card>
        </Link>

        {/*
          Las preguntas de ingreso van antes que las tareas del día.
          Son de una sola vez y completan su ficha: lo que el equipo necesita
          saber para leer todo lo demás. Una que la pharma agregue después
          aparece acá aunque la persona ya lleve meses adentro — por eso el
          texto no dice "al empezar".
        */}
        {data.onboarding.length > 0 && (
          <Card className="p-5 border-primary">
            <h2 className="font-semibold">Falta que nos cuentes algo</h2>
            <p className="text-sm text-fg-muted mt-0.5">
              {data.onboarding.length === 1
                ? 'Es una sola pregunta y se contesta una vez.'
                : `Son ${data.onboarding.length} preguntas y se contestan una sola vez.`}
            </p>
            <div className="mt-4 space-y-2">
              {data.onboarding.map((pregunta) => (
                <Link key={pregunta.id} to={`${base}/registrar/${pregunta.id}`} className="block">
                  <div className="flex items-center gap-3 p-3 rounded-md bg-primary-soft text-primary hover:brightness-95 transition-[filter]">
                    <span className="min-w-0 grow">
                      <span className="block font-semibold text-[15px]">{pregunta.label}</span>
                      {showTreatment && pregunta.program && (
                        <span className="block text-xs opacity-80 truncate">
                          {pregunta.program.brand ?? pregunta.program.name}
                        </span>
                      )}
                    </span>
                    <ChevronRight className="size-4 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {pendingToday.length > 0 && (
          <Card className="p-5">
            <h2 className="font-semibold">Para hoy</h2>
            <p className="text-sm text-fg-muted mt-0.5">
              {pendingToday.length === 1
                ? 'Te queda un registro por completar.'
                : `Te quedan ${pendingToday.length} registros por completar.`}
            </p>
            <div className="mt-4 space-y-2">
              {pendingToday.map((indicator) => (
                <Link key={indicator.id} to={`${base}/registrar/${indicator.id}`} className="block">
                  <div className="flex items-center gap-3 p-3 rounded-md bg-primary-soft text-primary hover:brightness-95 transition-[filter]">
                    <span className="min-w-0 grow">
                      <span className="block font-semibold text-[15px] truncate">
                        {shortLabel(indicator.label)}
                      </span>
                      {showTreatment && indicator.program && (
                        <span className="block text-xs opacity-80 truncate">
                          {indicator.program.brand ?? indicator.program.name}
                        </span>
                      )}
                    </span>
                    <ChevronRight className="size-4 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        <section>
          <h2 className="font-semibold mb-3">Tu programa</h2>
          <Card className="divide-y divide-border overflow-hidden">
            {data.menu
              // El reporte de efectos tiene su propio acceso acá abajo; si además
              // la institución lo puso en el menú, aparecería dos veces.
              .filter((item) => !(item.type === 'INTERNAL' && item.target === 'adverse-event'))
              .map((item) => (
                <MenuRow key={item.id} item={item} base={base} />
              ))}
          </Card>
        </section>

        {/*
          Lo demás que puede cargar, plegado.
          Arriba está "Para hoy", que es lo que hay que hacer. Esta lista es
          todo lo que el programa pregunta, y desplegada competía con aquella:
          catorce filas donde dos eran las que importaban, y ninguna señal de
          cuál era cuál. No se saca —alguien que tuvo un pico de dolor a las
          tres de la mañana tiene que poder registrarlo cuando le pasa— pero
          deja de disputarle la pantalla a lo del día.

          Lo pendiente no se repite acá: ya tiene su tarjeta arriba.
        */}
        {otros.length > 0 && (
          <Card className="p-5">
            <button
              type="button"
              onClick={() => setVerTodo((valor) => !valor)}
              className="w-full flex items-center gap-3 text-left"
            >
              <span className="min-w-0 grow">
                <span className="block font-semibold">Registrar otra cosa</span>
                <span className="block text-sm text-fg-muted mt-0.5">
                  {otros.length === 1
                    ? 'Hay una más que podés cargar cuando quieras'
                    : `Hay ${otros.length} más que podés cargar cuando quieras`}
                </span>
              </span>
              <ChevronRight
                className={cx(
                  'size-4 text-fg-subtle shrink-0 transition-transform',
                  verTodo && 'rotate-90',
                )}
              />
            </button>

            {verTodo && (
              <div className="mt-4 divide-y divide-border">
                {otros.map((indicator) => (
                  <Link
                    key={indicator.id}
                    to={`${base}/registrar/${indicator.id}`}
                    // Alto mínimo y no solo padding: con una etiqueta de una
                    // línea la fila quedaba en 35 px, justo por debajo de lo que
                    // se toca cómodo con el pulgar. La app se usa parada, en una
                    // sala de espera.
                    className="flex items-center gap-3 py-3 min-h-11 first:pt-0 last:pb-0"
                  >
                    <span className="min-w-0 grow">
                      <span className="block font-medium text-[15px] truncate">
                        {shortLabel(indicator.label)}
                      </span>
                      {showTreatment && indicator.program && (
                        <span className="block text-xs text-fg-muted truncate">
                          {indicator.program.brand ?? indicator.program.name}
                        </span>
                      )}
                    </span>

                    {indicator.doneToday && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-success shrink-0">
                        <Check className="size-3.5" />
                        Hoy
                      </span>
                    )}
                    <ChevronRight className="size-4 text-fg-subtle shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </Card>
        )}

        {data.nextEvent && (
          <Link to={`${base}/calendario`}>
            <Card className="p-4 flex items-center gap-3">
              <div className="size-10 rounded-md bg-accent-soft text-accent-fg grid place-items-center shrink-0">
                <CalendarDays className="size-5" />
              </div>
              <div className="min-w-0 grow">
                <p className="font-semibold text-[15px] truncate">{data.nextEvent.title}</p>
                <p className="text-sm text-fg-muted">
                  {formatDateLong(data.nextEvent.scheduledAt)} · {formatRelative(data.nextEvent.scheduledAt)}
                </p>
              </div>
              <ChevronRight className="size-4 text-fg-subtle shrink-0" />
            </Card>
          </Link>
        )}

        {data.tipOfTheDay && (
          <Card className="p-5 bg-surface-tinted border-transparent">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Lightbulb className="size-4" />
              Tip del día
            </div>
            <p className="font-semibold mt-2">{data.tipOfTheDay.title}</p>
            <p className="text-[15px] text-fg-muted mt-1 leading-relaxed">
              {data.tipOfTheDay.description}
            </p>
          </Card>
        )}


      </div>
    </div>
  );
}

function MenuRow({
  item,
  base,
}: {
  item: { label: string; icon: string; type: string; target: string };
  base: string;
}) {
  const to =
    item.type === 'TRACKER'
      ? `${base}/registrar/${item.target}`
      : item.type === 'CONTENT_CATEGORY'
        ? `${base}/contenidos?categoria=${item.target}`
        : item.type === 'CONTENT_ITEM'
          ? `${base}/contenidos/${item.target}`
          : item.type === 'INTERNAL' && item.target === 'adverse-event'
            ? `${base}/reportar`
            : null;

  const content = (
    <div className="flex items-center gap-3 px-4 py-3.5 hover:bg-surface transition-colors">
      <Icon name={item.icon} className="size-5 text-primary shrink-0" />
      <span className="text-[15px] grow">{item.label}</span>
      <ChevronRight className="size-4 text-fg-subtle shrink-0" />
    </div>
  );

  // Un item de tipo URL apunta afuera del programa; se abre en otra pestaña y
  // con rel noopener para que la página destino no pueda tocar esta.
  if (!to) {
    return (
      <a href={item.target} target="_blank" rel="noopener noreferrer" className="block">
        {content}
      </a>
    );
  }

  return (
    <Link to={to} className="block">
      {content}
    </Link>
  );
}

function ConsentPrompt({
  consent,
  onAccept,
  accepting,
}: {
  consent: { id: string; title: string; body: string };
  onAccept: (id: string) => void;
  accepting: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Card className="p-5 border-warning bg-warning-soft">
        <div className="flex gap-3">
          <AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold">Falta tu consentimiento</p>
            <p className="text-sm text-fg-muted mt-1 leading-relaxed">
              Para que podamos acompañarte necesitamos que leas y aceptes el consentimiento
              informado.
            </p>
            <Button size="sm" className="mt-4" onClick={() => setOpen(true)}>
              Leerlo ahora
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={consent.title}
        wide
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Ahora no
            </Button>
            <Button loading={accepting} onClick={() => onAccept(consent.id)}>
              Acepto participar
            </Button>
          </>
        }
      >
        <Markdown source={consent.body} />
      </Modal>
    </>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return 'Buenas noches';
  if (hour < 13) return 'Buen día';
  if (hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
}
